
define( 'system/js/emitter',[ 'jclass', 'jquery', 'system/js/socket' ], function( Class, $, Socket ) {
	'use strict';

	/*
	===============================================================================
	Class: Emitter
	===============================================================================
	*/

	var Emitter = Class._extend( {

		/**
		 * Method: init
		 * @param {Object} aOptions
		 */

		init: function( aOptions ) {
			this._socket = null;
			this._rooms = [ ];
			this._guests = { };
			this._parent = aOptions.parent;
			this._socket_guid = null;
			this._authenticated = false;
			this._broadcast_queue = [ ];
			this._enabled = aOptions.enabled || false;
		},

		/**
		 * Method: on
		 * @param {String} aEvent (can be a single event or multiple space-separated event names)
		 * @param {Function} aCallback
		*/

		on: function( aEvent, aCallback ) {
			var list = aEvent.split( ' ' ),
				self = this,
				i, len;

			if ( this._socket !== null && this._enabled === true ) {
				for ( i = 0, len = list.length; i < len; i++ ) {
					this._socket.on( list[ i ], function( aData ) {
						aCallback.call( self, aData );
					} );
				}
			}

			return this;
		},

		/**
		 * Method: one (one-time binding event)
		 * @param {String} aEvent (can be a single event or multiple space-separated event names)
		 * @param {Function} aCallback
		 */

		one: function( aEvent, aCallback ) {
			var list = aEvent.split( ' ' ),
				self = this,
				i, len;

			if ( this._socket !== null && this._enabled === true ) {
				for ( i = 0, len = list.length; i < len; i++ ) {
					this._socket.one( list[ i ], function( aData ) {
						aCallback.call( self, aData );
					} );
				}
			}

			return this;
		},

		/**
		 * Method: initialize
		 */

		initialize: function( ) {
			var app = this._parent,
				// Options will be a combination of system and module options, with module taking precedence
				options = $.extend( { }, app.getData( 'system.options.emitter' ), app.getData( 'module.data.emitter' ) ),
				self = this;

			if ( this._enabled === true && options.hasOwnProperty( 'rooms' ) && options.rooms.length > 0 ) {
				this._parent.verbose( 'Emitter enabled and has these rooms enabled:' + JSON.stringify( options.rooms ) );

				this._rooms = options.rooms;

				// Check if null, if so, create new socket
				if ( this._socket === null ) {
					this._socket = new Socket( {
						parent: this,
						session_id: options.session_id,
						url: options.url
					} );

					// Auto-connect
					this._socket.connect( );

					// When connected, automatically authenticate
					this._socket.on( 'connect', function( ) {
						self._socket.authenticate( );
					} );

					// When authenticated successfully, join allowed rooms
					this._socket.on( 'authenticated', function( aResponse ) {
						var i, len, queued;

						self._authenticated = true;
						self._socket_guid = aResponse.guid;

						// If there are broadcast events queued up awaiting authentication, fire them now
						if ( self._broadcast_queue.length > 0 ) {
							for ( i = 0, len = self._broadcast_queue.length; i < len; i++ ) {
								queued = self._broadcast_queue.shift( );

								self.broadcast( queued.type, queued.data );
							}
						}

						// Joining of rooms is performed automatically by the emitter server
						// TBD maybe remove join totally
					} );

					this._socket.on( 'syncguests', function( aRoomState ) {
						self._guests[ aRoomState.room ] = aRoomState.guests;
					} );

					app.bindEmitterEventListeners( );
				}
				else {
					this._socket.setSessionId( options.session_id );

					// Sync our room subscriptions
					this.broadcast( 'syncrooms', { session_id: options.session_id } );
				}
			}
			else {
				this._rooms = [ ];

				this.disconnect( );
			}
		},

		/**
		 * Method: disconnect
		 */

		disconnect: function( ) {
			if ( this._socket !== null ) {
				this._socket.disconnect( );

				this._socket = null;
			}

			return this;
		},

		/**
		 * Method: leave; leave all rooms currently joined to, but do not perform a disconnect
		 */

		leave: function( ) {
			var rooms = this.getRooms( ),
				i, len;

			if ( this._socket !== null ) {
				for ( i = 0, len = rooms.length; i < len; i++ ) {
					this.broadcast( 'leave', { room: rooms[ i ].name } );
				}
			}
		},

		/**
		 * Method: getRooms
		 */

		getRooms: function( ) {
			return this._rooms;
		},

		/**
		 * Method: getGuests
		 * @param {String} aRoom  Room name (optional), all rooms/guests returned otherwise
		 *
		 * @return {Array|Object} If given a name, then an array of guests; otherwise an object with all rooms and guests, keyed by room
		 */

		getGuests: function( aRoom ) {
			return this._guests;
		},

		/**
		 * Method: isUserPresent - given a user id, are they present guests in the account-type room
		 * @param {Int|String} aUserId
		 * @return {Boolean}
		 */

		isUserPresent: function( aUserId ) {
			var user_id = parseInt( aUserId, 10 ),
				account_room = this.getRoomByType( 'account' ),
				i, len;

			if ( account_room !== false ) {
				// Check if the account-type room name exists in our rooms array
				if ( this._guests.hasOwnProperty( account_room.name ) === true ) {
					// Find the user id
					for ( i = 0, len = this._guests[ account_room.name ].length; i < len; i++ ) {
						if ( parseInt( this._guests[ account_room.name ][ i ].user_id, 10 ) === user_id ) {
							return true;
						}
					}
				}
			}

			return false;
		},

		/**
		 * Method: getRoomByType
		 *
		 * @param {String} aType - A room-type (system, account, team, preview etc)
		 * @return {Object|false}
		 */

		getRoomByType: function( aType ) {
			var room_info, i, len;

			for ( i = 0, len = this._rooms.length; i < len; i++ ) {
				if ( this._rooms[ i ].type === aType ) {
					room_info = {
						name: this._rooms[ i ].name,
						type: this._rooms[ i ].type,
						guests: [ ]
					};

					if ( this._guests.hasOwnProperty( this._rooms[ i ].name ) === true ) {
						room_info.guests = this._guests[ this._rooms[ i ].name ];
					}

					return room_info;
				}
			}

			return false;
		},

		/**
		 * Method: getSocketGuid
		 */

		getSocketGuid: function( ) {
			return this._socket_guid;
		},

		/**
		 * Method: broadcast
		 * @param {String} aEvent
		 * @param {Object} aData
		 */

		broadcast: function( aEvent, aData ) {
			var data = aData || { };

			if ( this._socket !== null && this._authenticated === true && this._enabled === true ) {
				this._socket.broadcast( aEvent, data );
			}
			else {
				// Defer until we have a socket and are authenticated
				data = { type: aEvent, data: data };

				this._broadcast_queue.push( data );
			}

			return this;
		},

		/**
		 * Method: trigger (via AJAX, and then via emitter once verified)
		 * @param {String} aEvent
		 * @param {Object} aData
		 */

		trigger: function( aEvent, aData ) {
			var app = this._parent;

			if ( this._enabled === true ) {
				app.getConduit( ).ajax( {
					url: app.getData( 'module.data.base_url' ) + 'utility/emitter_trigger',
					data: {
						event: aEvent,
						data: aData,
						socketguid: this.getSocketGuid( )
					}
				} );
			}
		},

		/**
		 * Method: send a metric (storage) request directly via the emitter
		 * @param {String} aEvent
		 * @param {Object} aData
		 */

		metric: function( aEvent, aData ) {
			var data = $.extend( { type: aEvent }, aData );

			this.broadcast( 'metric', data );
		},

		/**
		 * Request enablement of the emitter (subject to what's deemed available via session)
		 * Performs no action if the emitter is _already_ enabled
		 */

		enable: function( ) {
			if ( this._enabled === false ) {
				this._enabled = true;

				this.initialize( );
			}
		}
	} );

	return Emitter;
} );

